import type { Declaration, GlossaProgram } from '../parser/ast.js';

/**
 * Το ίδιο όνομα δεν επιτρέπεται να δηλωθεί δύο φορές στο ΙΔΙΟ τμήμα
 * δηλώσεων (ΣΤΑΘΕΡΕΣ/ΜΕΤΑΒΛΗΤΕΣ) — αλλιώς η δεύτερη δήλωση σιωπηλά
 * αντικαθιστά τον τύπο της πρώτης, και το μόνο σύμπτωμα είναι ένα ασαφές
 * σφάλμα τύπου πολύ αργότερα, μακριά από την πραγματική αιτία. Βλ.
 * `Interpreter.requireNotDuplicateDeclaration` για το runtime αντίστοιχο.
 * Δεν χρειάζεται να περπατήσει το σώμα του προγράμματος καθόλου — η ίδια η
 * λίστα δηλώσεων αρκεί, άρα δεν υπάρχει "κλάδος που δεν θα εκτελούνταν" να
 * μας ξεφύγει.
 */
export interface DuplicateDeclaration {
  readonly name: string;
  readonly line: number;
  readonly column: number;
}

function findDuplicateInDeclarations(declarations: readonly Declaration[], reservedName?: string): DuplicateDeclaration | null {
  const seen = new Set<string>(reservedName === undefined ? [] : [reservedName]);
  for (const decl of declarations) {
    if (decl.kind === 'ConstantDeclaration') {
      if (seen.has(decl.name)) return { name: decl.name, line: decl.line, column: decl.column };
      seen.add(decl.name);
      continue;
    }
    for (const declarator of decl.declarators) {
      if (seen.has(declarator.name)) return { name: declarator.name, line: decl.line, column: decl.column };
      seen.add(declarator.name);
    }
  }
  return null;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ διπλή δήλωση (με σειρά εμφάνισης — κύριο πρόγραμμα
 * πρώτα, μετά κάθε υποπρόγραμμα), ή `null` αν δεν υπάρχει καμία. Κάθε
 * υποπρόγραμμα ελέγχεται με τις ΔΙΚΕΣ ΤΟΥ δηλώσεις (αυστηρά τοπική εμβέλεια
 * — βλ. `findUndeclaredGlossaReference`) — δεν ελέγχει σύγκρουση με το
 * όνομα κάποιας παραμέτρου, αφού η αντιστοίχιση παραμέτρου↔δηλωτή είναι η
 * ΚΑΝΟΝΙΚΗ, αναμενόμενη σύμβαση (βλ. `findParameterDeclarationInfo`), όχι
 * διπλή δήλωση. Για ΣΥΝΑΡΤΗΣΗ, το ΙΔΙΟ της το όνομα (χρησιμοποιείται και
 * ως η μεταβλητή επιστροφής) μετράει ΕΠΙΣΗΣ ως "ήδη δηλωμένο" μέσα στο
 * ΔΙΚΟ ΤΗΣ ΜΕΤΑΒΛΗΤΕΣ/ΣΤΑΘΕΡΕΣ — βλ. `Interpreter.registerDeclarations`.
 */
export function findDuplicateDeclaration(program: GlossaProgram): DuplicateDeclaration | null {
  const mainResult = findDuplicateInDeclarations(program.declarations);
  if (mainResult !== null) return mainResult;

  for (const sub of program.subprograms) {
    const reservedName = sub.kind === 'FunctionDeclaration' ? sub.name : undefined;
    const subResult = findDuplicateInDeclarations(sub.declarations, reservedName);
    if (subResult !== null) return subResult;
  }
  return null;
}
